import { useMemo, useState } from "react";
import { getFileUrl } from "../../api/file";
import { getUsers } from "../../api/user";
import { getEquipments } from "../../api/equipment";
import { getProjects } from "../../api/project";
import { createTask, deleteTask, getTasks, updateTask } from "../../api/task";
import { UserRoles } from "../user";

export function useTaskSection({ user, searchParams, attrs, isEdit }) {
  const [tasks, setTasks] = useState([]);

  const [searchUser, setSearchUser] = useState([]);
  const [searchProject, setSearchProject] = useState([]);
  const [searchEquipment, setSearchEquipment] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const dataIndex = (page - 1) * perPage;

  const sections = useMemo(() => {
    const userSearchOptions = searchUser.map((user) => {
      return {
        value: user.id,
        label: user.firstName + " " + (user.lastName || ""),
      };
    });

    const projectSearchOptions = searchProject.map((project) => {
      return {
        value: project.id,
        label: project.name,
      };
    });

    const equipmentSearchOptions = searchEquipment.map((equipment) => {
      return {
        value: equipment.id,
        label: equipment.name,
      };
    });

    const attachments = [];
    if (attrs && attrs.assignee) {
      userSearchOptions.push({
        value: attrs.assignee.id,
        label: attrs.assignee.firstName + " " + (attrs.assignee.lastName || ""),
      });
    }
    if (attrs && attrs.project) {
      projectSearchOptions.push({
        value: attrs.project.id,
        label: attrs.project.name,
      });
    }
    if (attrs && attrs.equipment) {
      equipmentSearchOptions.push({
        value: attrs.equipment.id,
        label: attrs.equipment.name,
      });
    }

    if (attrs && attrs.attachments) {
      attachments.push(
        ...(attrs.attachments || []).map((attachment) => {
          return {
            ...attachment,
            uid: attachment.id,
            name: attachment.originalName,
            status: "done",
            url: getFileUrl(attachment),
          };
        })
      );
    }

    return [
      {
        items: [
          {
            type: "text",
            label: "Task Name",
            name: "name",
            required: true,
          },
          {
            type: "async-select",
            label: "Assignee",
            placeholder: "Search assignee",
            name: "assigneeId",
            required: true,
            onSearch: async (str) => {
              const { results: users } = await getUsers({ q: str });
              setSearchUser(users);
            },
            options: userSearchOptions,
          },
          {
            type: "async-select",
            label: "Project",
            placeholder: "Search project",
            name: "projectId",
            required: true,
            onSearch: async (str) => {
              const { results: projects } = await getProjects({ q: str });
              setSearchProject(projects);
            },
            options: projectSearchOptions,
          },

          {
            type: "async-select",
            label: "Equipment",
            placeholder: "Search equipment",
            name: "equipmentId",
            required: false,
            onSearch: async (str) => {
              const { results: equipments } = await getEquipments({ q: str });
              setSearchEquipment(equipments);
            },
            options: equipmentSearchOptions,
          },
        ],
      },
      {
        items: [
          {
            type: "date",
            label: "Start Date",
            name: "startDate",
            showTime: true,
            format: "YYYY-MM-DD HH:mm a",
            required: true,
            col: 6,
          },
          {
            type: "date",
            label: "Due Date",
            name: "dueDate",
            showTime: true,
            format: "YYYY-MM-DD HH:mm a",
            required: true,
            col: 6,
          },
        ],
      },
      {
        items: [
          {
            type: "text-area",
            label: "Task Description",
            name: "description",
            col: 12,
            maxLen: 200,
            required: true,
          },
        ],
      },
      {
        items: [
          {
            additionalInfo: "Task specific documents, images etc.",
            type: "attachments",
            label: "Attachments (2MB)",
            name: "attachments",
            col: 12,
            maxLen: 200,
            fileList: attachments,
          },
        ],
      },
    ];
  }, [searchUser, searchProject, searchEquipment, attrs]);

  const fetchTasks = async ({
    page,
    q,
    perPage = 5,
    projectId,
    assigneeId,
  }) => {
    try {
      setIsLoading(true);
      const { total, results } = await getTasks({
        page,
        perPage,
        q,
        projectId,
        assigneeId,
      });
      setTotal(total);
      setTasks(results);
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
    setIsLoading(false);
  };

  const handleSubmit = async ({
    name,
    description,
    assigneeId,
    equipmentId,
    projectId,
    attachments,
    startDate,
    dueDate,
  }) => {
    const taskData = {
      name,
      description,
      assignee: { id: assigneeId },
      project: { id: projectId },
      startDate,
      dueDate,
      attachments:
        attachments && attachments.fileList && attachments.fileList.length
          ? attachments.fileList.map((file) => {
              if (!file.response) return file;
              return file.response.file;
            })
          : undefined,
    };

    if (equipmentId) {
      taskData.equipment = { id: equipmentId };
    }
    if (isEdit) {
      await updateTask({ ...taskData, id: attrs.id });
    } else {
      await createTask(taskData);
    }

    if (attachments) {
    }

    return fetchTasks({
      page,
      perPage,
      assigneeId: user.role === UserRoles.Employee ? user.id : null,
      projectId: searchParams ? searchParams.get("projectId") : undefined,
    });
  };

  const handleDelete = async (task) => {
    await deleteTask(task.id);
    fetchTasks({
      page,
      perPage,
      assigneeId: user.role === UserRoles.Employee ? user.id : null,
    });
  };

  return {
    tasks,
    fetchTasks,
    sections,
    isLoading,
    handleDelete,
    handleSubmit,
    searchProject,
    setSearchProject,

    pagination: {
      dataIndex,
      page,
      setPage,
      perPage,
      setPerPage,
      total,
      setTotal,
    },
    setIsLoading,
  };
}
